import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { aiJobAutofill } from '../lib/ai';
import { toast } from 'sonner';

/**
 * Smart Job Autofill — customer describes what they need in one sentence
 * and Gemini fills the create-job form. Renders as a collapsible section
 * inside the "New Request" dialog.
 *
 * @param {object} props
 * @param {(fields: object) => void} props.onFill — called with the parsed fields.
 */
export default function JobAutofillInput({ onFill }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    try {
      const fields = await aiJobAutofill(text.trim());
      if (!fields || Object.keys(fields).length === 0) {
        toast.error('Could not parse that. Try adding pickup and dropoff.');
      } else {
        onFill(fields);
        toast.success('Form filled — please review before submitting.');
        setText('');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI could not parse that request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-700">
        <Sparkles className="w-3.5 h-3.5" /> Describe It, We'll Fill It
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g., 'Pick up my Rx from Walgreens Mascoutah and drop it at 250 W Main. Recipient is my mom Sue born 3/15/1948.'"
        rows={2}
        className="text-sm bg-white resize-none"
        data-testid="ai-autofill-input"
      />
      <Button
        type="button"
        onClick={run}
        disabled={!text.trim() || loading}
        size="sm"
        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
        data-testid="ai-autofill-submit"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Thinking...</>
        ) : (
          <><Sparkles className="w-4 h-4 mr-1" /> Fill Form with AI</>
        )}
      </Button>
    </div>
  );
}
